// IM-JRN-014 (IM-GRID, IM-UX-147/148) — Grade leve, parecida com uma planilha: anos ou meses nas linhas, métricas nas
// colunas, setas e Enter navegam, colar um bloco copiado do Excel preenche a partir da célula, linhas podem ser
// incluídas e retiradas, totais e resultado se atualizam, o erro aparece na própria célula. Grava pela MESMA API e com as
// MESMAS validações da planilha (o servidor decide); o rascunho é compartilhado com a equipe (concorrência otimista).
import { useEffect, useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import {
  FINANCIAL_FIELDS, fmtBRL, MONTH_NAME, OFFERING_FIELDS, parseNumberBR, type FieldDef, type FinancialPeriodDto, type OfferingMetricDto, type OfferingRef, type PlanningIssue,
} from '@intelimove/contracts';
import { ApiError } from '../api/client';
import { useToast } from '../components/Toast';
import { planningApi, type GridRow } from '../data/planning';

export type GridMode = 'ANUAL' | 'MENSAL' | 'OFERTAS';
interface Line { key: string; year: string; month: number | null; offering: OfferingRef | null; id: string | null; version: number | null; values: Record<string, string> }
const DRAFT: Record<GridMode, string> = { ANUAL: 'GRADE_ANUAL', MENSAL: 'GRADE_MENSAL', OFERTAS: 'GRADE_OFERTAS' };
const show = (v: number | null | undefined) => (v === null || v === undefined ? '' : v.toLocaleString('pt-BR', { maximumFractionDigits: 2 }));
const num = (s: string | undefined) => { const n = parseNumberBR(s ?? ''); return typeof n === 'number' && !Number.isNaN(n) ? n : null; };
let seq = 0;
const k = () => `l${(seq += 1)}`;

function linesFor(mode: GridMode, year: number, periods: readonly FinancialPeriodDto[], offerings: readonly OfferingRef[], metrics: readonly OfferingMetricDto[], fields: readonly FieldDef[]): Line[] {
  const vals = (src: Record<string, unknown> | null) => Object.fromEntries(fields.map((f) => [f.key, src ? (f.kind === 'text' ? String(src[f.key] ?? '') : show(src[f.key] as number | null)) : '']));
  if (mode === 'ANUAL') {
    const annual = periods.filter((p) => p.month === null).sort((a, b) => a.year - b.year);
    const rows = annual.map((p) => ({ key: `a-${p.year}`, year: String(p.year), month: null, offering: null, id: p.id, version: p.version, values: vals(p as unknown as Record<string, unknown>) }));
    return rows.length ? rows : [year - 3, year - 2, year - 1].map((y) => ({ key: k(), year: String(y), month: null, offering: null, id: null, version: null, values: vals(null) }));
  }
  if (mode === 'MENSAL') {
    return Array.from({ length: 12 }, (_, i) => {
      const p = periods.find((x) => x.year === year && x.month === i + 1) ?? null;
      return { key: `m-${year}-${i + 1}`, year: String(year), month: i + 1, offering: null, id: p?.id ?? null, version: p?.version ?? null, values: vals(p as unknown as Record<string, unknown> | null) };
    });
  }
  return offerings.map((o) => {
    const m = metrics.find((x) => x.offeringId === o.id && x.year === year && x.month === null) ?? null;
    return { key: `o-${o.id}-${year}`, year: String(year), month: null, offering: o, id: m?.id ?? null, version: m?.version ?? null, values: vals(m as unknown as Record<string, unknown> | null) };
  });
}

export function PlanGrid({ mode, year, periods, offerings, metrics, onSaved }: {
  mode: GridMode; year: number; periods: readonly FinancialPeriodDto[]; offerings: readonly OfferingRef[]; metrics: readonly OfferingMetricDto[]; onSaved: () => void;
}) {
  const toast = useToast();
  const allFields = mode === 'OFERTAS' ? OFFERING_FIELDS : FINANCIAL_FIELDS;
  const [optional, setOptional] = useState(false);
  const fields = allFields.filter((f) => f.key !== 'note' && (mode === 'OFERTAS' || optional || f.required));
  const [lines, setLines] = useState<Line[]>(() => linesFor(mode, year, periods, offerings, metrics, allFields));
  const [issues, setIssues] = useState<PlanningIssue[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ version: number; by: string | null; at: string; payload: Record<string, unknown> } | null>(null);
  const [draftVersion, setDraftVersion] = useState(0);
  const [dirty, setDirty] = useState(false);
  const ref = useRef<HTMLTableElement>(null);
  useEffect(() => { setLines(linesFor(mode, year, periods, offerings, metrics, allFields)); setIssues([]); setDirty(false); }, [mode, year, periods, offerings, metrics]);
  useEffect(() => {
    const ac = new AbortController();
    planningApi.draft(DRAFT[mode], ac.signal).then((r) => {
      setDraftVersion(r.draft?.version ?? 0);
      setDraft(r.draft && (r.draft.payload as { year?: number }).year === year ? { version: r.draft.version, by: r.draft.updatedBy, at: r.draft.updatedAt, payload: r.draft.payload } : null);
    }).catch(() => undefined);
    return () => ac.abort();
  }, [mode, year]);

  const cols = mode === 'ANUAL' ? ['year', ...fields.map((f) => f.key)] : fields.map((f) => f.key);
  const label = (l: Line) => (mode === 'MENSAL' ? `${MONTH_NAME[l.month! - 1]} de ${l.year}` : mode === 'OFERTAS' ? l.offering!.name : `o ano ${l.year || 'novo'}`);
  const setCell = (r: number, key: string, v: string) => { setDirty(true); setLines((ls) => ls.map((l, i) => (i !== r ? l : key === 'year' ? { ...l, year: v } : { ...l, values: { ...l.values, [key]: v } }))); };
  const focus = (r: number, c: number) => ref.current?.querySelector<HTMLInputElement>(`input[data-r="${r}"][data-c="${c}"]`)?.focus();
  const onKey = (e: KeyboardEvent<HTMLInputElement>, r: number, c: number) => {
    const el = e.currentTarget;
    if (e.key === 'ArrowDown' || e.key === 'Enter') { e.preventDefault(); focus(r + 1, c); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); focus(r - 1, c); }
    else if (e.key === 'ArrowRight' && el.selectionStart === el.value.length) { e.preventDefault(); focus(r, c + 1); }
    else if (e.key === 'ArrowLeft' && el.selectionStart === 0 && el.selectionEnd === 0) { e.preventDefault(); focus(r, c - 1); }
  };
  /** Colar um bloco (Excel: tabulação entre colunas, quebra entre linhas) a partir da célula atual. */
  const onPaste = (e: ClipboardEvent<HTMLInputElement>, r: number, c: number) => {
    const text = e.clipboardData.getData('text/plain');
    if (!/[\t\n]/.test(text.trim())) return;
    e.preventDefault();
    const block = text.replace(/\r/g, '').replace(/\n$/, '').split('\n').map((x) => x.split('\t'));
    setDirty(true);
    setLines((ls) => {
      const out = [...ls];
      block.forEach((cells, i) => {
        const ri = r + i;
        if (ri >= out.length) {
          if (mode !== 'ANUAL') return;
          out.push({ key: k(), year: '', month: null, offering: null, id: null, version: null, values: Object.fromEntries(allFields.map((f) => [f.key, ''])) });
        }
        cells.forEach((v, j) => {
          const key = cols[c + j];
          if (!key) return;
          const l = out[ri]!;
          out[ri] = key === 'year' ? { ...l, year: v.trim() } : { ...l, values: { ...l.values, [key]: v.trim() } };
        });
      });
      return out;
    });
  };
  const rowsPayload = (): GridRow[] => lines.map((l, i) => ({ line: i + 1, values: {
    ...(mode === 'OFERTAS' ? { offering: l.offering!.id } : {}), year: l.year.trim() === '' ? null : l.year.trim(), month: l.month,
    ...Object.fromEntries(Object.entries(l.values).map(([key, v]) => [key, v.trim() === '' ? null : v.trim()])),
  } }))
    // Linha toda vazia (só ano/mês/oferta) não é dado: o servidor ignora; aqui não enviamos.
    .filter((x) => Object.entries(x.values).some(([key, v]) => !['year', 'month', 'offering'].includes(key) && v !== null));
  const dataset = mode === 'OFERTAS' ? 'OFERTAS' : 'FINANCEIRO';
  const errOf = (r: number, key: string) => {
    const lab = key === 'year' ? 'Ano' : key === 'month' ? 'Mês' : allFields.find((f) => f.key === key)?.label;
    return issues.find((i) => i.line === r + 1 && i.column === lab && i.severity === 'ERRO') ?? null;
  };
  const rowErrors = issues.filter((i) => i.severity === 'ERRO' && (i.line === null || !cols.some((c) => { const lab = c === 'year' ? 'Ano' : allFields.find((f) => f.key === c)?.label; return lab === i.column; })));
  const run = async (apply: boolean) => {
    const rows = rowsPayload();
    if (!rows.length) { toast('Preencha ao menos uma linha.', 'error'); return; }
    setBusy(apply ? 'save' : 'check');
    try {
      const r = await planningApi.save({ dataset, rows, strategy: 'ATUALIZAR', apply });
      setIssues(r.issues);
      if (r.errors) { toast(`${r.errors} ${r.errors === 1 ? 'célula precisa' : 'células precisam'} de correção.`, 'error'); return; }
      if (apply) {
        toast(`Base atualizada: ${r.inserts} ${r.inserts === 1 ? 'período novo' : 'períodos novos'}, ${r.updates} ${r.updates === 1 ? 'atualizado' : 'atualizados'}, ${r.unchanged} sem mudança.`);
        if (draftVersion) await planningApi.discardDraft(DRAFT[mode]).catch(() => undefined);
        setDraft(null); setDraftVersion(0); setDirty(false);
        onSaved();
      } else toast(r.warnings ? `Tudo certo, com ${r.warnings} ${r.warnings === 1 ? 'aviso' : 'avisos'}.` : 'Tudo certo. Pode salvar.');
    } catch (e) {
      if (e instanceof ApiError && e.status === 422 && (e.details as { issues?: PlanningIssue[] })?.issues) setIssues((e.details as { issues: PlanningIssue[] }).issues);
      toast(e instanceof ApiError ? e.message : 'Não foi possível salvar.', 'error');
    } finally { setBusy(null); }
  };
  const saveDraft = async () => {
    setBusy('draft');
    try { const r = await planningApi.saveDraft(DRAFT[mode], { year, lines: lines.map((l) => ({ year: l.year, month: l.month, offeringId: l.offering?.id ?? null, values: l.values })) }, draftVersion); setDraftVersion(r.version); setDirty(false); toast('Rascunho salvo. Qualquer pessoa da organização pode continuar.'); }
    catch (e) { toast(e instanceof ApiError ? e.message : 'Não foi possível salvar o rascunho.', 'error'); }
    finally { setBusy(null); }
  };
  const resume = () => {
    const ls = (draft!.payload.lines as Array<{ year: string; month: number | null; offeringId: string | null; values: Record<string, string> }>) ?? [];
    setLines(ls.map((l) => ({ key: k(), year: l.year, month: l.month, offering: offerings.find((o) => o.id === l.offeringId) ?? null, id: null, version: null, values: l.values })).filter((l) => mode !== 'OFERTAS' || l.offering));
    setDraft(null); setDirty(true);
  };
  const totals = useMemo(() => Object.fromEntries(fields.filter((f) => f.kind === 'money' || f.kind === 'int').map((f) => {
    const xs = lines.map((l) => num(l.values[f.key])).filter((x): x is number => x !== null);
    return [f.key, xs.length ? Math.round(xs.reduce((s, x) => s + x * 100, 0)) / 100 : null];
  })), [lines, fields]);
  const result = (l: Line) => { const r = num(l.values.revenue), d = num(l.values.operatingExpenses); return r !== null && d !== null ? r - d : null; };
  const removable = mode === 'ANUAL';
  const remove = async (i: number) => {
    const l = lines[i]!;
    if (l.id && l.version) {
      if (!window.confirm(`Arquivar ${l.year}? O período sai da base, mas o histórico continua guardado.`)) return;
      try { await planningApi.archivePeriod(l.id, l.version); toast(`${l.year} arquivado.`); onSaved(); } catch (e) { toast(e instanceof ApiError ? e.message : 'Não foi possível arquivar.', 'error'); }
      return;
    }
    setLines((ls) => ls.filter((_, j) => j !== i)); setDirty(true);
  };
  return (
    <div className="pl-grid-block">
      {draft && (
        <div className="xp-block pl-draft" role="note">
          <b>Há um rascunho salvo{draft.by ? ` por ${draft.by}` : ''} em {new Date(draft.at).toLocaleString('pt-BR')}.</b>
          <div className="pl-inline-actions"><button type="button" className="secondary" onClick={resume}>Continuar o rascunho</button>
            <button type="button" className="link-btn" onClick={async () => { await planningApi.discardDraft(DRAFT[mode]).catch(() => undefined); setDraft(null); setDraftVersion(0); }}>Descartar</button></div>
        </div>
      )}
      <div className="pl-grid-tools">
        {mode !== 'OFERTAS' && <label className="check-inline"><input type="checkbox" checked={optional} onChange={(e) => setOptional(e.target.checked)} /> Mostrar campos opcionais</label>}
        <span className="cell-sub">Dica: copie um bloco do Excel e cole na primeira célula. Setas e Enter navegam.</span>
      </div>
      {rowErrors.length > 0 && <ul className="pl-errors" role="alert">{rowErrors.slice(0, 5).map((i, n) => <li key={n}>{i.line ? `Linha ${i.line}: ` : ''}{i.message} {i.expected}</li>)}</ul>}
      <div className="pl-grid-wrap" role="region" aria-label="Grade de valores" tabIndex={0}>
        <table className="pl-grid" ref={ref}>
          <thead>
            <tr>
              <th scope="col">{mode === 'MENSAL' ? 'Mês' : mode === 'OFERTAS' ? 'Oferta' : 'Ano'}</th>
              {fields.map((f) => <th key={f.key} scope="col" title={f.help}>{f.short}{f.required ? <span aria-hidden="true"> *</span> : null}{f.required ? <span className="sr-only"> (obrigatório)</span> : null}</th>)}
              {mode !== 'OFERTAS' && <th scope="col" title="Faturamento menos despesas operacionais">Resultado</th>}
              {removable && <th scope="col"><span className="sr-only">Ações</span></th>}
            </tr>
          </thead>
          <tbody>
            {lines.map((l, r) => (
              <tr key={l.key}>
                {mode === 'ANUAL' ? (
                  <td>{(() => { const e = errOf(r, 'year'); const id = `g-${l.key}-year`; return <><input data-r={r} data-c={0} inputMode="numeric" className="pl-cell pl-year" value={l.year} aria-label={`Ano da linha ${r + 1}`} aria-invalid={!!e} aria-describedby={e ? id : undefined}
                    onChange={(ev) => setCell(r, 'year', ev.target.value)} onKeyDown={(ev) => onKey(ev, r, 0)} onPaste={(ev) => onPaste(ev, r, 0)} />{e && <small id={id} className="pl-cell-err">{e.message}</small>}</>; })()}</td>
                ) : <th scope="row">{mode === 'MENSAL' ? MONTH_NAME[l.month! - 1] : l.offering!.name}</th>}
                {fields.map((f, j) => {
                  const c = mode === 'ANUAL' ? j + 1 : j;
                  const e = errOf(r, f.key);
                  const id = `g-${l.key}-${f.key}`;
                  return (
                    <td key={f.key}>
                      <input data-r={r} data-c={c} className="pl-cell" inputMode="decimal" value={l.values[f.key] ?? ''} aria-label={`${f.label} de ${label(l)}`} aria-invalid={!!e} aria-describedby={e ? id : undefined}
                        onChange={(ev) => setCell(r, f.key, ev.target.value)} onKeyDown={(ev) => onKey(ev, r, c)} onPaste={(ev) => onPaste(ev, r, c)} />
                      {e && <small id={id} className="pl-cell-err">{e.message}</small>}
                    </td>
                  );
                })}
                {mode !== 'OFERTAS' && <td className="pl-num">{fmtBRL(result(l))}</td>}
                {removable && <td><button type="button" className="link-btn" onClick={() => remove(r)} aria-label={`Retirar a linha de ${label(l)}`}>Retirar</button></td>}
              </tr>
            ))}
          </tbody>
          {mode !== 'ANUAL' && (
            <tfoot>
              <tr><th scope="row">Total</th>{fields.map((f) => <td key={f.key} className="pl-num">{f.kind === 'money' ? fmtBRL(totals[f.key] ?? null) : f.kind === 'int' && f.key !== 'activeCustomers' && totals[f.key] !== null ? String(totals[f.key]) : ''}</td>)}
                {mode !== 'OFERTAS' && <td className="pl-num">{totals.revenue != null && totals.operatingExpenses != null ? fmtBRL(totals.revenue - totals.operatingExpenses) : '—'}</td>}</tr>
            </tfoot>
          )}
        </table>
      </div>
      <div className="pl-inline-actions">
        {removable && <button type="button" className="secondary" onClick={() => { const ys = lines.map((l) => Number(l.year)).filter(Boolean); setLines((ls) => [{ key: k(), year: String((ys.length ? Math.min(...ys) : year) - 1), month: null, offering: null, id: null, version: null, values: Object.fromEntries(allFields.map((f) => [f.key, ''])) }, ...ls]); setDirty(true); }}>Adicionar ano</button>}
        <button type="button" className="secondary" disabled={!!busy || !dirty} onClick={saveDraft}>{busy === 'draft' ? 'Salvando…' : 'Salvar rascunho'}</button>
        <button type="button" className="secondary" disabled={!!busy} onClick={() => run(false)}>{busy === 'check' ? 'Validando…' : 'Validar'}</button>
        <button type="button" className="xp-primary petrol" disabled={!!busy} onClick={() => run(true)}>{busy === 'save' ? 'Salvando…' : 'Validar e salvar na base'}</button>
      </div>
    </div>
  );
}

